import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { History, Lock, Swords, Users } from "lucide-react";
import { useMemo, useState } from "react";

import { ActivityHeatmapStrip } from "../../../components/profile/ActivityHeatmapStrip";
import { Button } from "../../../components/ui";
import { ClubSection } from "../../../components/profile/ClubSection";
import { MatchHistoryTab } from "../../../components/profile/MatchHistoryTab";
import { ProfileSidebar } from "../../../components/profile/ProfileSidebar";
import { useSession } from "../../../lib/auth-client";
import { statusMap } from "../../../lib/status";
import { trpc } from "../../../lib/trpc";

type ProfileTab = "clubs" | "history";

export const Route = createFileRoute("/app/users/$slug")({
  staticData: { title: "User Profile" },
  validateSearch: (search: Record<string, unknown>): { guest?: boolean; tab?: ProfileTab } => ({
    guest: search.guest === true || search.guest === "true" ? true : undefined,
    tab: search.tab === "history" ? "history" : undefined,
  }),
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const { slug } = Route.useParams();
  const { guest, tab: searchTab } = Route.useSearch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProfileTab>(searchTab ?? "clubs");

  const { data: user, isLoading, error } = trpc.user.getProfile.useQuery({ slug });
  const { data: session } = useSession();
  const userId = user?.id;
  const { data: accounts } = trpc.linkedAccount.list.useQuery(
    { userId: userId! },
    { enabled: !!userId && (!!user?.isPublic || (!guest && session?.user?.id === userId)) },
  );

  const isOwnProfile = !guest && session?.user?.id === user?.id;

  // Fetch clubs for the profile user
  const { data: clubs, isLoading: clubsLoading } = trpc.user.getPublicClubs.useQuery(
    { userId: userId! },
    { enabled: !!userId && !!user && (user.isPublic || isOwnProfile), staleTime: 60_000 },
  );

  // Clubs where the viewer can see stats (for match history tab)
  const viewableClubs = useMemo(
    () => (clubs ?? []).filter((c) => c.privacy !== "private" || c.isViewerMember),
    [clubs],
  );

  if (isLoading) return <ProfileSkeleton />;

  if (error || !user) {
    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 flex flex-col items-center justify-center py-24">
        <h2 className="text-xl font-bold mb-2">User Not Found</h2>
        <p className="text-surface-400 text-sm mb-6">
          This user doesn&apos;t exist or has been removed.
        </p>
      </div>
    );
  }

  const status = statusMap[user.status] ?? statusMap.offline;

  // Private profile — sidebar + lock state
  if (!user.isPublic && !isOwnProfile) {
    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-6 md:gap-8 animate-fade-in">
          <ProfileSidebar user={user} status={status} isOwnProfile={false} />

          <div className="relative rounded-xl overflow-hidden border border-surface-700/50 bg-surface-800/50 py-16 flex flex-col items-center text-center">
            <div className="absolute inset-0 noise-overlay opacity-30" />
            <div className="relative flex flex-col items-center">
              <div className="size-12 rounded-full bg-surface-700/60 flex items-center justify-center mb-4">
                <Lock className="size-5 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-surface-200 mb-1">
                This profile is private
              </h3>
              <p className="text-sm text-surface-400 max-w-xs">
                Competitive stats, match history, and linked accounts are hidden.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-6 md:gap-8 animate-fade-in">
        {/* ── Left sidebar ── */}
        <ProfileSidebar
          user={user}
          status={status}
          accounts={accounts}
          isOwnProfile={isOwnProfile}
        />

        {/* ── Right main column ── */}
        <div className={`flex flex-col gap-5 min-w-0 ${activeTab === "history" ? "h-full" : ""}`}>
          {/* Activity heatmap */}
          <ActivityHeatmapStrip
            userId={userId!}
            className="rounded-xl border border-surface-600/50 p-4"
            title="Match Activity"
          />

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-surface-700/40">
            <button
              type="button"
              onClick={() => setActiveTab("clubs")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                activeTab === "clubs"
                  ? "border-brand-500 text-white"
                  : "border-transparent text-surface-400 hover:text-surface-200"
              }`}
            >
              <Users className="size-4" />
              Clubs ({clubs?.length ?? 0})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                activeTab === "history"
                  ? "border-brand-500 text-white"
                  : "border-transparent text-surface-400 hover:text-surface-200"
              }`}
            >
              <History className="size-4" />
              Match History
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "clubs" ? (
            !clubsLoading && clubs && clubs.length > 0 ? (
              <div className="space-y-3">
                {clubs.map((club) => (
                  <ClubSection
                    key={club.id}
                    club={club}
                    userId={userId!}
                    isViewerMember={club.isViewerMember}
                  />
                ))}
              </div>
            ) : !clubsLoading ? (
              isOwnProfile ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <div className="relative mb-5">
                    <div className="absolute -inset-3 rounded-full bg-brand-500/8 blur-xl" />
                    <div className="relative size-14 rounded-full bg-surface-800 border border-surface-700/50 flex items-center justify-center">
                      <Swords className="size-6 text-brand-400" />
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-surface-200 mb-1">Ready to compete?</p>
                  <p className="text-xs text-surface-500 mb-5">
                    Join a club to start building your rank
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate({ to: "/app/clubs" })}
                  >
                    Browse Clubs
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <div className="relative mb-5">
                    <div className="absolute -inset-3 rounded-full bg-brand-500/8 blur-xl" />
                    <div className="relative size-14 rounded-full bg-surface-800 border border-surface-700/50 flex items-center justify-center">
                      <Users className="size-6 text-brand-400" />
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-surface-300 mb-1">No clubs yet</p>
                  <p className="text-xs text-surface-500">
                    This player hasn&apos;t joined any clubs
                  </p>
                </div>
              )
            ) : null
          ) : (
            <div className="flex-1 min-h-0 flex flex-col">
              <MatchHistoryTab clubs={viewableClubs} userId={userId!} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-6 md:gap-8">
        {/* Sidebar skeleton */}
        <div className="rounded-xl border border-surface-700/30 bg-surface-850 overflow-hidden">
          <div className="h-20 bg-surface-800/30 animate-pulse" />
          <div className="flex flex-col items-center px-5 -mt-14 pb-5">
            <div className="size-28 rounded-full bg-surface-700 animate-pulse ring-[3px] ring-surface-850" />
            <div className="h-6 w-32 bg-surface-700 rounded animate-pulse mt-3" />
            <div className="h-5 w-16 bg-surface-700/40 rounded-full animate-pulse mt-2" />
            <div className="h-4 w-48 bg-surface-700/30 rounded animate-pulse mt-3" />
            <div className="h-3.5 w-28 bg-surface-700/20 rounded animate-pulse mt-2" />
          </div>
        </div>

        {/* Main column skeleton */}
        <div className="space-y-5">
          <div className="h-[110px] rounded-xl bg-surface-850 border border-surface-700/30 animate-pulse" />
          <div className="flex rounded-lg overflow-hidden border border-surface-700/50">
            <div className="flex-1 py-2.5 bg-surface-850" />
            <div className="flex-1 py-2.5 bg-surface-850" />
          </div>
          <div className="space-y-3">
            <div className="h-32 rounded-xl bg-surface-850 border border-surface-700/30 animate-pulse" />
            <div className="h-32 rounded-xl bg-surface-850 border border-surface-700/30 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}
